import { useState } from "react";
import { useProfile } from "@/contexts/ProfileContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { FcGoogle } from "react-icons/fc";
import { useToast } from "@/hooks/use-toast";
import { Mail, Lock, User, Sparkles, Eye, EyeOff, KeyRound, Loader2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import { BRAND } from "@/config/brand";

interface ProfileModalProps {
  onClose?: () => void;
  defaultTab?: string;
}

export function ProfileModal({ onClose, defaultTab = "login" }: ProfileModalProps) {
  const { profile, setProfile } = useProfile();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(defaultTab);

  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaCode, setMfaCode] = useState("");

  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const [registerEmail, setRegisterEmail] = useState("");
  const [registerPassword, setRegisterPassword] = useState("");
  const [registerFirstName, setRegisterFirstName] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);

  const [schoolCode, setSchoolCode] = useState("");
  const [schoolName, setSchoolName] = useState("");

  const [errorMessage, setErrorMessage] = useState("");
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage("");

    try {
      const loginDeviceId = localStorage.getItem("memnoraDeviceId") || "";
      const loginHeaders: Record<string, string> = { "Content-Type": "application/json" };
      if (loginDeviceId) loginHeaders["X-Device-Id"] = loginDeviceId;

      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: loginHeaders,
        credentials: "include",
        body: JSON.stringify({ email: loginEmail, password: loginPassword }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || data.message || "Invalid email or password");
      }

      if (data?.mfaRequired) {
        setMfaRequired(true);
        setMfaCode("");
        setIsLoading(false);
        return;
      }

      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });

      const profileId = data.id;
      const profileRes = await fetch(`/api/profile/${profileId}`, { credentials: "include" }).catch(() => null);
      if (profileRes?.ok) {
        const profileData = await profileRes.json();
        localStorage.setItem("memnora-profile-id", profileData.id);
        setProfile(profileData);
      } else {
        localStorage.setItem("memnora-profile-id", profileId);
        setProfile({
          id: profileId,
          displayName: data.firstName || data.email?.split("@")[0] || "User",
          isSaved: false,
          createdAt: data.createdAt || new Date().toISOString(),
        });
      }

      queryClient.invalidateQueries({ queryKey: ["/api/magnora/threads"] });
      queryClient.invalidateQueries({ queryKey: ["/api/magnora/persistent"] });
      queryClient.invalidateQueries({ queryKey: ["/api/salvation/status"] });

      toast({
        title: "Welcome back!",
        description: `Signed in as ${data.firstName || data.email}. Memnora is ready.`,
      });
      onClose?.();
    } catch (error: any) {
      setErrorMessage(error.message);
      toast({
        title: "Sign in failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  type LoginUser = {
    id: string;
    email?: string | null;
    firstName?: string | null;
    createdAt?: string;
  };

  type LoginResponse = LoginUser & { mfaRequired?: boolean };

  const finishLoginWithUser = async (data: LoginUser) => {
    queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });

    const profileId = data.id;
    const profileRes = await fetch(`/api/profile/${profileId}`, { credentials: "include" }).catch(() => null);
    if (profileRes?.ok) {
      const profileData = await profileRes.json();
      localStorage.setItem("memnora-profile-id", profileData.id);
      setProfile(profileData);
    } else {
      localStorage.setItem("memnora-profile-id", profileId);
      setProfile({
        id: profileId,
        displayName: data.firstName || data.email?.split("@")[0] || "User",
        isSaved: false,
        createdAt: data.createdAt || new Date().toISOString(),
      });
    }

    queryClient.invalidateQueries({ queryKey: ["/api/magnora/threads"] });
    queryClient.invalidateQueries({ queryKey: ["/api/magnora/persistent"] });
    queryClient.invalidateQueries({ queryKey: ["/api/salvation/status"] });

    toast({
      title: "Welcome back!",
      description: `Signed in as ${data.firstName || data.email}. Memnora is ready.`,
    });
    onClose?.();
  };

  const handleMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage("");
    try {
      const res = await fetch("/api/auth/login/mfa", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ code: mfaCode }),
      });
      const data = (await res.json()) as LoginUser & { error?: string };
      if (!res.ok) {
        if (/expired/i.test(data?.error || "")) {
          setMfaRequired(false);
          setMfaCode("");
          throw new Error("Your login session expired. Please sign in again.");
        }
        throw new Error(data.error || "Invalid code");
      }
      await finishLoginWithUser(data);
      setMfaRequired(false);
      setMfaCode("");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Sign in failed";
      setErrorMessage(message);
      toast({ title: "Sign in failed", description: message, variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!termsAccepted) {
      setErrorMessage("You must accept the Terms of Service and Privacy Policy to continue.");
      return;
    }
    setIsLoading(true);
    setErrorMessage("");

    try {
      const deviceId = localStorage.getItem("memnoraDeviceId") || "";
      const regHeaders: Record<string, string> = { "Content-Type": "application/json" };
      if (deviceId) regHeaders["X-Device-Id"] = deviceId;

      const res = await fetch("/api/auth/register", {
        method: "POST",
        headers: regHeaders,
        credentials: "include",
        body: JSON.stringify({
          email: registerEmail,
          password: registerPassword,
          firstName: registerFirstName || undefined,
          termsAccepted,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || data.message || "Registration failed");
      }

      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });

      localStorage.setItem("memnora-profile-id", data.id);
      setProfile({
        id: data.id,
        displayName: data.firstName || data.email?.split("@")[0] || "User",
        isSaved: false,
        createdAt: data.createdAt || new Date().toISOString(),
      });

      queryClient.invalidateQueries({ queryKey: ["/api/magnora/threads"] });
      queryClient.invalidateQueries({ queryKey: ["/api/magnora/persistent"] });

      toast({
        title: "Welcome!",
        description: `Account created. Memnora is ready to meet you.`,
      });
      onClose?.();
    } catch (error: any) {
      setErrorMessage(error.message);
      toast({
        title: "Registration failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const needsName = !profile;

  const handleSchoolCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage("");

    try {
      const body: Record<string, string> = { code: schoolCode.trim().toUpperCase() };
      if (needsName && schoolName.trim()) {
        body.displayName = schoolName.trim();
      }

      const res = await apiRequest("POST", "/api/schools/join", body);
      const data = await res.json();

      if (data.profile) {
        localStorage.setItem("memnora-profile-id", data.profile.id);
        setProfile({
          id: data.profile.id,
          displayName: data.profile.displayName,
          isSaved: false,
          isSubscribed: data.isSubscribed || false,
          createdAt: data.profile.createdAt || new Date().toISOString(),
        });
      } else if (profile && data.isSubscribed) {
        setProfile({ ...profile, isSubscribed: true });
      }

      queryClient.invalidateQueries({ queryKey: ["/api/schools/my-schools"] });
      queryClient.invalidateQueries({ queryKey: ["/api/magnora/threads"] });
      queryClient.invalidateQueries({ queryKey: ["/api/magnora/persistent"] });
      queryClient.invalidateQueries({ queryKey: ["/api/salvation/status"] });

      if (profile?.id || data.profile?.id) {
        const pid = data.profile?.id || profile?.id;
        try {
          const profileRes = await fetch(`/api/profile/${pid}`, { credentials: "include" });
          if (profileRes.ok) {
            const fullProfile = await profileRes.json();
            if (fullProfile.isSubscribed === undefined && data.isSubscribed) {
              fullProfile.isSubscribed = true;
            }
            setProfile(fullProfile);
          }
        } catch (_e) { console.warn("[ProfileModal] Profile refresh after school join:", _e); }
      }

      const joinedSchoolName = data.school?.name || "the school";
      const schoolId = data.school?.id || data.membership?.schoolId;

      if (data.alreadyMember) {
        toast({ title: `You're already a member of ${joinedSchoolName}` });
      } else {
        toast({ title: `Joined ${joinedSchoolName} successfully!`, description: `Role: ${data.membership?.role || "Member"}` });
      }

      onClose?.();

      if (schoolId) {
        const role = data.membership?.role;
        const isAdmin = role === "ADMIN" || role === "CURRICULUM_LEAD";
        setTimeout(() => navigate(`/school/${schoolId}/${isAdmin ? "admin" : "teaching"}`), 300);
      }
    } catch (error: any) {
      setErrorMessage(error.message);
      toast({
        title: "Failed to join school",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage("");
    try {
      const res = await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: forgotEmail }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || data.message || "Request failed");
      setForgotSent(true);
      toast({ title: "Request received", description: data.message });
    } catch (error: any) {
      setErrorMessage(error.message);
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleContinueAsGuest = () => {
    onClose?.();
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[10000] flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Sparkles className="h-6 w-6 text-primary" />
          </div>
          <CardTitle className="text-2xl">Welcome to The Third Temple</CardTitle>
          <CardDescription>
            Sign in to save your journey with Memnora, or continue as a guest.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {(activeTab === "login" || activeTab === "register") && (
            <div className="space-y-4 mb-4">
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => {
                  const plan = new URLSearchParams(window.location.search).get("plan");
                  const params = plan ? `?plan=${encodeURIComponent(plan)}` : "";
                  window.location.href = `/api/auth/google${params}`;
                }}
                data-testid="button-google-signin"
              >
                <FcGoogle className="h-4 w-4 mr-2" />
                Continue with Google
              </Button>
              <p className="text-center text-[11px] text-muted-foreground">
                By continuing, you agree to our{" "}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground">
                  Terms
                </a>{" "}
                and{" "}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground">
                  Privacy Policy
                </a>
                .
              </p>
              <div className="flex items-center gap-3">
                <div className="h-px flex-1 bg-border" />
                <span className="text-xs text-muted-foreground">or</span>
                <div className="h-px flex-1 bg-border" />
              </div>
            </div>
          )}
          <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setErrorMessage(""); }} className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="login" data-testid="tab-login">Sign In</TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register">Register</TabsTrigger>
              <TabsTrigger value="school" data-testid="tab-school">School Code</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              {showForgotPassword ? (
                <div className="space-y-4 mt-4">
                  {forgotSent ? (
                    <div className="text-center space-y-3" data-testid="text-forgot-sent">
                      <Mail className="h-8 w-8 mx-auto text-amber-500" />
                      <p className="text-sm text-muted-foreground">
                        If an account exists with that email, we've sent a link to reset your password. It expires in 1 hour.
                      </p>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-xs"
                        onClick={() => { setShowForgotPassword(false); setForgotSent(false); setForgotEmail(""); setErrorMessage(""); }}
                        data-testid="button-back-to-login"
                      >
                        Back to Sign In
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={handleForgotPassword} className="space-y-4">
                      <p className="text-sm text-muted-foreground">
                        Enter your email address and we'll help you regain access to your account.
                      </p>
                      <div className="space-y-2">
                        <Label htmlFor="forgot-email">Email</Label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            id="forgot-email"
                            type="email"
                            placeholder="your@email.com"
                            value={forgotEmail}
                            onChange={(e) => setForgotEmail(e.target.value)}
                            className="pl-9"
                            required
                            data-testid="input-forgot-email"
                          />
                        </div>
                      </div>
                      {errorMessage && (
                        <p className="text-sm text-destructive" data-testid="text-forgot-error">{errorMessage}</p>
                      )}
                      <Button type="submit" className="w-full" disabled={isLoading} data-testid="button-forgot-submit">
                        {isLoading ? "Sending..." : "Reset Password"}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="w-full text-xs text-muted-foreground"
                        onClick={() => { setShowForgotPassword(false); setErrorMessage(""); }}
                        data-testid="button-back-to-login"
                      >
                        Back to Sign In
                      </Button>
                    </form>
                  )}
                </div>
              ) : mfaRequired ? (
                <form onSubmit={handleMfaSubmit} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-mfa-code">6-digit code from your authenticator</Label>
                    <Input
                      id="login-mfa-code"
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="123456"
                      value={mfaCode}
                      onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ""))}
                      required
                      autoFocus
                      data-testid="input-login-mfa-code"
                    />
                  </div>
                  {errorMessage && (
                    <p className="text-sm text-destructive" data-testid="text-login-mfa-error">{errorMessage}</p>
                  )}
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={isLoading || mfaCode.length !== 6}
                    data-testid="button-login-mfa-submit"
                  >
                    {isLoading ? "Verifying..." : "Verify and sign in"}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full"
                    onClick={() => { setMfaRequired(false); setMfaCode(""); setErrorMessage(""); }}
                    data-testid="button-login-mfa-cancel"
                  >
                    Cancel
                  </Button>
                </form>
              ) : (
                <form onSubmit={handleEmailLogin} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="your@email.com"
                        value={loginEmail}
                        onChange={(e) => setLoginEmail(e.target.value)}
                        className="pl-9"
                        required
                        data-testid="input-login-email"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="login-password">Password</Label>
                      <button
                        type="button"
                        className="text-xs text-amber-500 hover:underline"
                        onClick={() => { setShowForgotPassword(true); setErrorMessage(""); setForgotEmail(loginEmail); }}
                        data-testid="button-forgot-password"
                      >
                        Forgot password?
                      </button>
                    </div>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="login-password"
                        type={showLoginPassword ? "text" : "password"}
                        placeholder="Your password"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        className="pl-9 pr-10"
                        required
                        data-testid="input-login-password"
                      />
                      <button
                        type="button"
                        tabIndex={-1}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        onClick={() => setShowLoginPassword(!showLoginPassword)}
                        data-testid="button-toggle-login-password"
                        aria-label={showLoginPassword ? "Hide password" : "Show password"}
                      >
                        {showLoginPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  {errorMessage && activeTab === "login" && !showForgotPassword && (
                    <p className="text-sm text-destructive" data-testid="text-login-error">{errorMessage}</p>
                  )}
                  <Button type="submit" className="w-full" disabled={isLoading} data-testid="button-login">
                    {isLoading ? "Signing in..." : "Sign In"}
                  </Button>
                </form>
              )}
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="register-name">Name (optional)</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="register-name"
                      placeholder="Your name"
                      value={registerFirstName}
                      onChange={(e) => setRegisterFirstName(e.target.value)}
                      className="pl-9"
                      data-testid="input-register-name"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="register-email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="register-email"
                      type="email"
                      placeholder="your@email.com"
                      value={registerEmail}
                      onChange={(e) => setRegisterEmail(e.target.value)}
                      className="pl-9"
                      required
                      data-testid="input-register-email"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="register-password">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="register-password"
                      type={showRegisterPassword ? "text" : "password"}
                      placeholder="At least 8 characters"
                      value={registerPassword}
                      onChange={(e) => setRegisterPassword(e.target.value)}
                      className="pl-9 pr-10"
                      required
                      minLength={8}
                      data-testid="input-register-password"
                    />
                    <button
                      type="button"
                      tabIndex={-1}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                      data-testid="button-toggle-register-password"
                      aria-label={showRegisterPassword ? "Hide password" : "Show password"}
                    >
                      {showRegisterPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Checkbox
                    id="register-terms"
                    checked={termsAccepted}
                    onCheckedChange={(checked) => setTermsAccepted(checked === true)}
                    className="mt-0.5"
                    data-testid="checkbox-register-terms"
                  />
                  <Label htmlFor="register-terms" className="text-xs font-normal text-muted-foreground leading-snug">
                    I agree to the{" "}
                    <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground">
                      Terms of Service
                    </a>{" "}
                    and{" "}
                    <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground">
                      Privacy Policy
                    </a>
                  </Label>
                </div>
                {errorMessage && activeTab === "register" && (
                  <p className="text-sm text-destructive" data-testid="text-register-error">{errorMessage}</p>
                )}
                <Button type="submit" className="w-full" disabled={isLoading || !termsAccepted} data-testid="button-register">
                  {isLoading ? "Creating account..." : "Create Account"}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="school">
              <form onSubmit={handleSchoolCode} className="space-y-4 mt-4">
                <p className="text-xs text-muted-foreground">
                  Enter the access code provided by your school administrator to join with your assigned role.
                </p>
                {needsName && (
                  <div className="space-y-2">
                    <Label htmlFor="school-name">Your Name</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="school-name"
                        placeholder="Enter your name"
                        value={schoolName}
                        onChange={(e) => setSchoolName(e.target.value)}
                        className="pl-9"
                        maxLength={50}
                        data-testid="input-school-name"
                      />
                    </div>
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="school-code">Access Code</Label>
                  <div className="relative">
                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="school-code"
                      placeholder="e.g. A1B2C3D4"
                      value={schoolCode}
                      onChange={(e) => setSchoolCode(e.target.value.toUpperCase())}
                      className="pl-9 text-center tracking-widest font-mono"
                      maxLength={20}
                      data-testid="input-school-code"
                    />
                  </div>
                </div>
                {errorMessage && activeTab === "school" && (
                  <p className="text-sm text-destructive" data-testid="text-school-error">{errorMessage}</p>
                )}
                <Button
                  type="submit"
                  className="w-full"
                  disabled={isLoading || schoolCode.trim().length === 0 || (needsName && schoolName.trim().length < 2)}
                  data-testid="button-join-school"
                >
                  {isLoading ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Joining...</>
                  ) : (
                    "Join School"
                  )}
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          <div className="mt-6 pt-4 border-t">
            <Button
              variant="ghost"
              className="w-full text-muted-foreground"
              onClick={handleContinueAsGuest}
              data-testid="button-continue-guest"
            >
              Continue as Guest
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
